import type {ReactElement, ReactNode} from 'react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import styles from './ai-chat-window.module.scss';
import {CircularProgressLoader, LOADER_COLOR} from '@Components/circular-progress-loader';
import '@Global/create-from-scratch';
import type {AiChatWindowProps, ChatMessage, ChatMessagePart, ChatOnLoadParams, ImagePart, MessagesResponse, RequestConfig, TextPartPurpose} from './ai-chat-window.types';
import {CHAT_NOT_FOUND_ERROR_CODE, ChatMessagePartType} from './ai-chat-window.types';
import {useChatStream} from '@Components/ai-chat-window/hooks/useChatStream';
import {useChatOnLoadFill} from '@Components/ai-chat-window/hooks/useChatOnLoadFill';
import {
  buildRequestConfig,
  convertMessagesArrayToHashmap,
  createUserRequestParts,
  findLastUserMessage,
  getAiChatCompletedStateForInProgressChat,
} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {isUserPremium, isUserPremiumPlus, isUserTrial} from '@Libraries/user.library';
import type {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {convertImagePartToPromptAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {aiPromptBoxActions} from '@Components/ai-prompt-box/ai-prompt-box.reducer';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import ChatMessages from '@Components/ai-chat-window/components/chat-messages-display/chat-messages';
import {AiChatWindowPromptBox} from '@Components/ai-chat-window/components/ai-chat-window-prompt-box/ai-chat-window-prompt-box';
import {
  handleChatNotFound,
  handleConnectionIssue,
  getAiChatMessageCost,
  openAiChatAuthenticateModal,
  showAiChatPremiumOnlyFeatureDialog,
  showConvertTrialForAiChat,
  showPurchaseCreditsDialog,
} from '@Libraries/ai-chat.library';
import {useDispatch, useSelector} from 'react-redux';
import type {RootState} from '@/store';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {scrollToBottom} from '@Utils/dom.util';
import {AiChatStartSource, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';

export function AiChatWindow({currentlyOpenedChatId, onChatStart, ...props}: AiChatWindowProps): ReactElement {
  const {state: aiChatState, actions} = useAiChat();
  const {registerDisconnectChatStream} = actions;
  const dispatch = useDispatch();

  const {initialPrompt, initialAttachments} = useChatOnLoadFill(props.chatOnLoadParams);

  const [chatLoading, setChatLoading] = useState(false);
  const [historyFetchComplete, setHistoryFetchComplete] = useState(false);
  const hasInitiatedSendOnLoadRef = useRef<boolean>(false);
  const [editSendInProgressMessageKeyMap, setEditSendInProgressMessageKeyMap] = useState<Record<string, string | null>>({});
  const editSendInProgressMessageKey = currentlyOpenedChatId ? (editSendInProgressMessageKeyMap[currentlyOpenedChatId] ?? null) : null;
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const promptBoxRef = useRef<HTMLDivElement | null>(null);
  const [promptBoxHeight, setPromptBoxHeight] = useState(0);
  const isPremiumUser = isUserPremium() || isUserPremiumPlus();

  const handleEditMessageApplied = useCallback((): void => {
    if (currentlyOpenedChatId) {
      setEditSendInProgressMessageKeyMap((prev) => ({...prev, [currentlyOpenedChatId]: null}));
    }
    actions.setEditingUserMessageId(null);
  }, [currentlyOpenedChatId, actions]);

  const {setMessages, startGenerationStream, stopGeneration, messageSessionUids, setIsRefreshing, setHasWriteAccess, disconnect, resumeWorkflow} = useChatStream({
    onChatStart,
    modeSpecificProps: props.modeSpecificProps,
    refreshChatHistory: props.refreshChatHistory,
    setCurrentChatId: props.setCurrentChatId,
    refreshCredits: props.refreshCredits,
    chatMessagesContainerRef: messagesRef,
    onGenerationComplete: (messages: Record<string, ChatMessage>) => {
      void props.onChatComplete?.(messages);
    },
    onUpdateHistoryItem: actions.updateChatHistoryItem,
    onSetSearchTags: actions.setSearchTagsForChat,
    onNewChat: props.onNewChat,
    onEditMessageApplied: handleEditMessageApplied,
    mode: props.mode,
  });

  const {messages, isStreaming, isRefreshing, hasWriteAccess, isDeleted} = useSelector((state: RootState) => {
    if (!currentlyOpenedChatId) {
      return {messages: {}, isStreaming: false, isRefreshing: false, error: null, hasWriteAccess: true, isDeleted: false};
    }

    const chatState = state.aiChatWindow[currentlyOpenedChatId];
    return {
      messages: chatState?.messages ?? {},
      isStreaming: chatState?.isStreaming ?? false,
      isRefreshing: chatState?.isRefreshing ?? false,
      error: chatState?.error ?? null,
      hasWriteAccess: chatState?.hasWriteAccess ?? true,
      isDeleted: chatState?.isDeleted ?? false,
    };
  });

  useEffect(() => {
    registerDisconnectChatStream(disconnect);
  }, [disconnect, registerDisconnectChatStream]);

  const getChatHistory = useCallback(async (): Promise<void> => {
    if (isStreaming) {
      return;
    }

    if (!currentlyOpenedChatId) {
      return;
    }

    try {
      setChatLoading(true);
      const response = (await window.PMW.readLocal('ai/messages', {chatId: currentlyOpenedChatId})) as MessagesResponse;
      setMessages(currentlyOpenedChatId, convertMessagesArrayToHashmap(response.history));
      setChatLoading(false);

      if (response.activeMessageSessionUid) {
        messageSessionUids.set(currentlyOpenedChatId, response.activeMessageSessionUid);
      }

      setHasWriteAccess(currentlyOpenedChatId, response.hasWriteAccess);
      dispatch(aiChatWindowActions.setIsDeleted({chatId: currentlyOpenedChatId, isDeleted: response.isDeleted ?? false}));
      dispatch(aiChatWindowActions.setUseSmartAssetSelection({chatId: currentlyOpenedChatId, useSmartAssetSelection: response.useSmartAssetSelection ?? null}));
      actions.setSearchTagsForChat(currentlyOpenedChatId, response.searchTags ?? []);

      if (response.activeMessageSessionUid) {
        await handleRefreshedInProgressChat(response.activeMessageSessionUid);
      }
    } catch (e: any) {
      console.error('Failed to fetch chat history:', e);

      if (e?.status === 0) {
        handleConnectionIssue();
        return;
      }

      if (e?.code === CHAT_NOT_FOUND_ERROR_CODE) {
        handleChatNotFound();
        return;
      }
    } finally {
      setChatLoading(false);
      setIsRefreshing(currentlyOpenedChatId, false);
      setHistoryFetchComplete(true);
    }
  }, [currentlyOpenedChatId, messageSessionUids]);

  useEffect(() => {
    const element = promptBoxRef.current;
    if (!element) {
      return;
    }

    setPromptBoxHeight(element.offsetHeight);

    const observer = new ResizeObserver((entries) => {
      const newHeight = entries[0].contentRect.height;
      setPromptBoxHeight(newHeight);
    });

    observer.observe(element);

    return (): void => {
      observer.unobserve(element);
    };
  }, [promptBoxRef]);

  useEffect(() => {
    if (!currentlyOpenedChatId) {
      setHistoryFetchComplete(true);
      return;
    }

    const hasCachedMessages = Object.keys(messages).length > 0;

    if (!hasCachedMessages) {
      void getChatHistory();
    } else {
      setHistoryFetchComplete(true);
    }
  }, [currentlyOpenedChatId, getChatHistory]);

  useEffect(() => {
    scrollToBottom(messagesRef.current, 'instant');
  }, [currentlyOpenedChatId, messagesRef]);

  useEffect(() => {
    const scrollToMessage = (): void => {
      const urlParams = new URLSearchParams(window.location.search);
      const messageId = urlParams.get('messageId');

      if (!messageId || !messagesRef.current || Object.keys(messages).length === 0) {
        return;
      }

      setTimeout(() => {
        if (!messagesRef.current) {
          return;
        }
        const messageElement = messagesRef.current.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
        if (messageElement) {
          messageElement.scrollIntoView({behavior: 'smooth', block: 'center'});
          messageElement.style.backgroundColor = 'rgba(255, 255, 0, 0.2)';
          setTimeout(() => {
            messageElement.style.backgroundColor = '';
          }, 2000);
        }
      }, 100);
    };

    if (currentlyOpenedChatId && Object.keys(messages).length > 0) {
      scrollToMessage();
    }
  }, [currentlyOpenedChatId, messages]);

  useEffect(() => {
    if (hasInitiatedSendOnLoadRef.current) {
      return;
    }
    if (!props.chatOnLoadParams?.sendOnLoad) {
      return;
    }
    if (!props.chatOnLoadParams.userRequestParts || props.chatOnLoadParams.userRequestParts.length === 0) {
      return;
    }
    if (!historyFetchComplete) {
      return;
    }

    hasInitiatedSendOnLoadRef.current = true;

    const initializeChatOnLoad = async (chatOnLoadParams: ChatOnLoadParams): Promise<void> => {
      const userRequestParts = chatOnLoadParams.userRequestParts ?? [];
      const requestConfig = buildRequestConfig(messages, undefined, chatOnLoadParams.requestInstruction);
      await handleChatSent(userRequestParts, requestConfig);
    };

    void initializeChatOnLoad(props.chatOnLoadParams);
  }, [historyFetchComplete, messages, props.chatOnLoadParams?.sendOnLoad]);

  const handleRefreshedInProgressChat = async (activeMessageSessionUid: string): Promise<void> => {
    if (!currentlyOpenedChatId) {
      return;
    }

    setIsRefreshing(currentlyOpenedChatId, true);

    setIsRefreshing(currentlyOpenedChatId, true);

    const chatCompletedStateForInProgressChatResponse = await getAiChatCompletedStateForInProgressChat(currentlyOpenedChatId, activeMessageSessionUid);

    if (chatCompletedStateForInProgressChatResponse) {
      props.refreshCredits();

      setMessages(chatCompletedStateForInProgressChatResponse.idchat, convertMessagesArrayToHashmap(chatCompletedStateForInProgressChatResponse.history));

      await props.onChatComplete?.(convertMessagesArrayToHashmap(chatCompletedStateForInProgressChatResponse.history));
    }
  };

  const onMessageRefMount = useCallback((container: HTMLDivElement): void => {
    if (!container) {
      return;
    }

    messagesRef.current = container;
  }, []);

  const buyCreditsAndResumeChat = async (userRequestParts: ChatMessagePart[]) => {
    const purchaseData = await showPurchaseCreditsDialog();

    if (!purchaseData?.creditsV2) {
      return;
    }

    props.refreshCredits();

    if (purchaseData.creditsV2 >= getAiChatMessageCost()) {
      await startGenerationStream(currentlyOpenedChatId, userRequestParts);
    }
  };

  const handleOutOfCredits = useCallback(
    (userRequestParts: ChatMessagePart[]): void => {
      if (isUserTrial()) {
        showConvertTrialForAiChat((): void => {
          void buyCreditsAndResumeChat(userRequestParts);
        });

        return;
      }

      if (!isPremiumUser) {
        showAiChatPremiumOnlyFeatureDialog(props.userHasChatHistory);
        return;
      }

      void buyCreditsAndResumeChat(userRequestParts);
    },
    [showPurchaseCreditsDialog, startGenerationStream, props.onSendMessage]
  );

  const handleAnonymousUserChatRequest = useCallback(
    async (userRequestParts: ChatMessagePart[], requestConfig?: RequestConfig): Promise<void> => {
      const isLoggedIn = await openAiChatAuthenticateModal(props.onLoginSuccessActions);
      if (!isLoggedIn) {
        return;
      }

      if (!isUserPremium() && !isUserPremiumPlus()) {
        showAiChatPremiumOnlyFeatureDialog(props.userHasChatHistory);
        return;
      }

      await refreshTeamCreditsInStore();
      const {credits} = await getTeamCredits();
      if (credits < getAiChatMessageCost()) {
        handleOutOfCredits(userRequestParts);
        return;
      }

      await startGenerationStream(currentlyOpenedChatId, userRequestParts, requestConfig);
    },
    [startGenerationStream, handleOutOfCredits, props.onSendMessage]
  );

  const handleChatSent = useCallback(
    async (userRequestParts: ChatMessagePart[], requestConfig?: RequestConfig): Promise<void> => {
      if (!props.isUserLoggedIn) {
        await handleAnonymousUserChatRequest(userRequestParts, requestConfig);
        return;
      }

      if (!isPremiumUser && !aiChatState.isPaygUserWithAiChatAccess) {
        showAiChatPremiumOnlyFeatureDialog(props.userHasChatHistory);
        return;
      }

      if (props.credits < getAiChatMessageCost()) {
        handleOutOfCredits(userRequestParts);
        return;
      }

      await startGenerationStream(currentlyOpenedChatId, userRequestParts, requestConfig);
    },
    [startGenerationStream, handleOutOfCredits, props.onSendMessage, handleAnonymousUserChatRequest]
  );

  const handleRetry = useCallback(async (): Promise<void> => {
    const lastUserMessage = findLastUserMessage(messages);

    if (!lastUserMessage?.parts) {
      console.error('Retry failed: Could not find the last user message to retry.');
      return;
    }

    if (!currentlyOpenedChatId) {
      console.error('Retry failed: No current chat ID available.');
      return;
    }

    await handleChatSent(lastUserMessage.parts);
  }, [messages, handleChatSent]);

  const generate = useCallback(
    async (prompt: string, promptAttachments?: AiPromptBoxAttachments, requestConfig?: RequestConfig, purpose?: TextPartPurpose): Promise<void> => {
      const userRequestParts = createUserRequestParts(prompt, promptAttachments?.items ?? [], purpose);
      const finalRequestConfig = buildRequestConfig(messages, requestConfig);

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_START, {[GA4EventParamName.SOURCE]: AiChatStartSource.AI_HOMEPAGE});

      await handleChatSent(userRequestParts, finalRequestConfig);
    },
    [handleChatSent, messages]
  );

  const handleRegenerate = (prompt?: string, requestConfig?: RequestConfig, purpose?: TextPartPurpose): void => {
    const promptToUse = prompt ?? window.i18next.t('pmwjs_make_another_version');
    void generate(promptToUse, undefined, requestConfig, purpose);
  };

  const handleEditSend = useCallback(
    async (editedMessageId: string, newText: string): Promise<void> => {
      if (!currentlyOpenedChatId) {
        return;
      }
      if (currentlyOpenedChatId) {
        setEditSendInProgressMessageKeyMap((prev) => ({...prev, [currentlyOpenedChatId]: editedMessageId}));
      }

      const messageKeys = Object.keys(messages);
      const editedIndex = messageKeys.indexOf(editedMessageId);

      if (editedIndex === -1) {
        return;
      }

      const previousMessageId = editedIndex > 0 ? messageKeys[editedIndex - 1] : undefined;
      const originalMessage = messages[editedMessageId];
      const originalNonTextParts = (originalMessage?.parts ?? []).filter((part) => part.type !== ChatMessagePartType.TEXT);

      const userRequestParts: ChatMessagePart[] = [{type: ChatMessagePartType.TEXT, text: newText}, ...originalNonTextParts];
      try {
        actions.setEditingUserMessageId(null);
        await startGenerationStream(currentlyOpenedChatId, userRequestParts, undefined, {
          editedMessageId,
          previousMessageId,
        });
      } catch {
        if (currentlyOpenedChatId) {
          setEditSendInProgressMessageKeyMap((prev) => ({...prev, [currentlyOpenedChatId]: null}));
        }
        actions.setEditingUserMessageId(null);
        showMessageGrowl({
          text: window.i18next.t('pmwjs_failed_to_send_edited_message'),
          key: 'failedToSendEditedMessage',
          interval: 2000,
          showCloseIcon: true,
          type: GROWL_TYPE.DANGER,
        });
      }
    },
    [currentlyOpenedChatId, startGenerationStream, actions, messages]
  );

  const onStop = async (): Promise<void> => {
    if (!currentlyOpenedChatId) {
      return;
    }

    setIsRefreshing(currentlyOpenedChatId, false);
    scrollToBottom(messagesRef.current);

    await stopGeneration(currentlyOpenedChatId);
  };

  const handleReattach = useCallback(
    (part: ImagePart, previewUrl?: string): void => {
      const attachment = convertImagePartToPromptAttachment(part, previewUrl);
      if (!attachment) {
        return;
      }

      dispatch(aiPromptBoxActions.addAttachments({id: props.id, attachments: [attachment]}));
      dispatch(aiPromptBoxActions.updateAiPromptBox({id: props.id, value: {promptBoxState: PromptBoxState.ACTIVE}}));
    },
    [dispatch, props.id]
  );

  const renderFloatingPromptBox = (): ReactElement => {
    const isPromptBoxInvisible = chatLoading || !hasWriteAccess || isDeleted;

    return (
      <div
        ref={promptBoxRef}
        className={`${props.hasFloatingPromptBox ? '_position-absolute' : ''} _full-width ${styles.promptBoxBottomPositioning} ${isPromptBoxInvisible ? '_invisible' : '_visible'}`}
      >
        <AiChatWindowPromptBox
          shouldShowScrollToBottomButton={!chatLoading}
          containerClassname={`${styles.promptBoxContainer} ${currentlyOpenedChatId ? styles.hasMessages : ''}`}
          isMessageInProgress={isStreaming || isRefreshing}
          isReturningUser={props.userHasChatHistory}
          hasChatHistory={Object.keys(messages).length > 0}
          chatId={currentlyOpenedChatId}
          messages={messages}
          id={props.id}
          onStopGeneration={onStop}
          onGenerate={generate}
          onRegenerate={handleRegenerate}
          showProgressiveBlur={!!currentlyOpenedChatId}
          initialPrompt={initialPrompt}
          initialAttachments={initialAttachments}
        />
      </div>
    );
  };

  const renderMessages = (): ReactElement => {
    if (chatLoading) {
      return (
        <div className={`_full-width _full-height flex-center ${styles.loaderContainer}`}>
          <CircularProgressLoader color={LOADER_COLOR.AI_DARK} classes={'spacing-m-l-0'} />
        </div>
      );
    }

    return (
      <ChatMessages
        key={currentlyOpenedChatId}
        heightAdjustmentForFloatingPromptBox={props.hasFloatingPromptBox ? promptBoxHeight : undefined}
        isMessageStreaming={isStreaming}
        waitingForRefreshedChatToFinish={isRefreshing}
        messages={messages}
        chatId={currentlyOpenedChatId}
        bubbleClassNames={styles.bubble}
        onRetry={handleRetry}
        onMessageRefMount={onMessageRefMount}
        onRegenerate={handleRegenerate}
        onGenerate={generate}
        onReattach={handleReattach}
        onEditSend={handleEditSend}
        editSendInProgressMessageKey={editSendInProgressMessageKey}
        onResumeWorkflow={resumeWorkflow}
      />
    );
  };

  const getDeletedChatTagForSuperUser = (): ReactNode => {
    if (isDeleted) {
      return (
        <div className={`spacing-p-4 flexbox flex-center gap-2`}>
          <Icon className={`content-danger`} icon="icon-delete" size={IconSize.SIZE_ICON_32} type={IconType.DANGER} shape={IconShape.NONE} />
          <Text val={window.i18next.t('pmwjs_chat_has_been_deleted')} className={'content-danger'} size={TextSize.XLARGE} />
        </div>
      );
    }

    return null;
  };

  const getContent = (): ReactElement => {
    return (
      <>
        {renderMessages()}
        {renderFloatingPromptBox()}
        {getDeletedChatTagForSuperUser()}
      </>
    );
  };

  const getContentWrapperClassNames = (): string => {
    if (isDeleted) {
      return styles.deletedChat;
    }

    if (!hasWriteAccess) {
      return styles.readOnlyAccessChat;
    }

    return '';
  };

  return (
    <div ref={shellRef} className={`_position-relative ${styles.shell}`}>
      <div className={`${styles.stage} ${styles.withMessages} ${getContentWrapperClassNames()}`}>{getContent()}</div>
    </div>
  );
}
